import { api, type CommercialResponse } from '../lib/api';
import { useResource } from '../lib/useResource';
import { formatNumber } from '../lib/dataState';
import { formatInstant } from '../lib/format';

/**
 * Funil comercial inbound.
 *
 * Duas coisas que esta tela precisa acertar:
 *
 * 1. **A cadeia de atribuição diz o que FALTA** (AT-129). Uma cadeia parcial
 *    apresentada como completa faria o dono atribuir uma reunião a um post que
 *    talvez não tenha nada a ver — e decisões de conteúdo sairiam desse número.
 *
 * 2. **`unknown` não é "desqualificado"** (AT-123). Dado ausente significa que a
 *    descoberta continua, e a tela precisa dizer isso com todas as letras — a
 *    leitura preguiçosa é descartar quem só não se explicou ainda.
 */

const STATE_LABEL: Record<string, string> = {
  discovered: 'Descoberto',
  qualifying: 'Em qualificação',
  qualified: 'Qualificado',
  meeting_booked: 'Reunião agendada',
  opted_out: 'Pediu para parar',
  nurture: 'Em nutrição',
  disqualified: 'Desqualificado',
  routed_to_support: 'Roteado para suporte',
};

const FIT_LABEL: Record<string, string> = {
  unknown: 'Desconhecido',
  strong: 'Forte',
  partial: 'Parcial',
  out_of_profile: 'Fora do perfil',
  existing_customer: 'Cliente existente',
};

const FOLLOWUP_STATE_LABEL: Record<string, string> = {
  draft: 'Rascunho',
  scheduled: 'Agendado',
  sent: 'Enviado',
  blocked: 'Bloqueado',
  cancelled: 'Cancelado',
};

export function Commercial() {
  const resource = useResource<CommercialResponse>(() => api.commercial(), []);

  if (resource.kind === 'loading') {
    return (
      <div className="text-sm" aria-busy="true">
        <h1 className="text-2xl font-bold">Vendas e agendamento</h1>
        <p className="mt-2 text-ink-muted">Carregando…</p>
      </div>
    );
  }

  if (resource.kind === 'error') {
    return (
      <div className="max-w-3xl space-y-4 text-sm">
        <h1 className="text-2xl font-bold">Vendas e agendamento</h1>
        <div className="mt-4 rounded border border-danger-border bg-danger-surface p-4">
          <p className="font-medium">Não foi possível carregar o funil.</p>
          <p className="mt-1">{resource.message}</p>
          <p className="mt-2 text-xs text-ink-faint">
            Nenhuma cadeia de atribuição aparece como completa aqui: não saber nunca é o mesmo que
            estar completo. Código: {resource.code}
          </p>
        </div>
      </div>
    );
  }

  const { data } = resource;

  return (
    <div className="space-y-4 text-sm">
      <header>
        <h1 className="text-2xl font-bold">Vendas e agendamento</h1>
        <p className="mt-1 text-ink-muted">
          {formatNumber(data.total)} lead(s) · {formatNumber(data.opted_out_count)} pediram para
          parar. Todo lead nasce de intenção que a pessoa demonstrou: não existe prospecção.
        </p>
      </header>

      {data.total === 0 ? (
        <p className="mt-6 rounded-[11px] border border-line bg-surface-sunken p-4 text-ink-muted">
          Nenhum lead. Lead só nasce de uma interação em que a pessoa demonstrou intenção comercial.
        </p>
      ) : (
        <ul className="mt-6 space-y-4">
          {data.leads.map((l) => (
            <li
              key={l.lead_id}
              className={`rounded-card border border-line bg-card p-4 shadow-card ${
                l.opted_out ? 'border-l-4 border-l-line-strong bg-surface-sunken/60' : ''
              }`}
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="font-medium">{l.contact_handle}</span>
                <span className="text-xs uppercase tracking-wide text-ink-muted">
                  {STATE_LABEL[l.state] ?? l.state}
                </span>
              </div>

              <p className="mt-1 text-xs text-ink-muted">
                Perfil: {FIT_LABEL[l.icp_fit] ?? l.icp_fit}
                {l.icp_fit === 'unknown' ? (
                  /* AT-123 com todas as letras: desconhecido NÃO é descartado. */
                  <span className="text-ink-faint">
                    {' '}
                    (a descoberta continua; desconhecido não desqualifica)
                  </span>
                ) : l.icp_evidence.length > 0 ? (
                  ` · ${l.icp_evidence.join(', ')}`
                ) : null}
              </p>

              {l.opted_out ? (
                <p className="mt-2 text-xs text-ink-muted">
                  Pediu para parar em{' '}
                  {l.opted_out_at ? formatInstant(l.opted_out_at) : 'data não registrada'}. Nenhum
                  follow-up sai, nem para avisar que vamos parar.
                </p>
              ) : null}

              {/* ---- Origem: a prova de que a intenção partiu da pessoa ----- */}
              <div className="mt-3 rounded border border-line bg-surface-muted p-2">
                <p className="text-xs font-medium">O que a pessoa escreveu</p>
                <p className="mt-1 text-xs">{l.origin_interaction.text}</p>
                <p className="mt-1 text-xs text-ink-faint">
                  {formatInstant(l.origin_interaction.received_at)}
                </p>
              </div>

              {/* ---- Follow-ups -------------------------------------------- */}
              {l.follow_ups.length > 0 ? (
                <ul className="mt-3 space-y-1">
                  {l.follow_ups.map((f) => (
                    <li key={f.follow_up_id} className="text-xs">
                      <span className="text-ink-muted">
                        #{formatNumber(f.sequence)} {FOLLOWUP_STATE_LABEL[f.state] ?? f.state}:
                      </span>{' '}
                      {f.new_value}
                      {f.blocked_reason ? (
                        <span className="text-warn-ink">: {f.blocked_reason}</span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : null}

              {/* ---- Reuniões ---------------------------------------------- */}
              {l.meetings.map((m) => (
                <div key={m.meeting_id} className="mt-3 rounded border border-line p-2">
                  <p className="text-xs font-medium">
                    Reunião {m.state} · {formatInstant(m.scheduled_for)} ·{' '}
                    {formatNumber(m.duration_minutes)} min
                  </p>
                  <p className="mt-1 text-xs">{m.handoff_context}</p>
                  {m.handoff_open_items.length > 0 ? (
                    <p className="mt-1 text-xs text-warn-ink">
                      Em aberto: {m.handoff_open_items.join('; ')}
                    </p>
                  ) : null}
                </div>
              ))}

              {/* ---- Atribuição -------------------------------------------- */}
              <div className="mt-3 text-xs">
                {l.attribution.complete ? (
                  <p className="text-ok-ink">
                    Cadeia completa: conteúdo → publicação → interação → lead → reunião.
                  </p>
                ) : (
                  <p className="text-warn-ink">
                    Cadeia <strong>incompleta</strong>. Falta:{' '}
                    {l.attribution.missing_links.join(', ')}. Tratar como completa faria uma reunião
                    ser atribuída a um post que talvez não tenha relação.
                  </p>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
